import { useState, useRef, useEffect, useCallback } from "react";

// Add this component to your file
const VirtualizedList = ({
  items,
  renderItem,
  chunkSize = 200,
  filterKey
}: {
  items: any[],
  renderItem: (item: any) => React.ReactNode,
  chunkSize?: number,
  filterKey?: string
}) => {
  const [visibleCount, setVisibleCount] = useState(chunkSize);
  const loaderRef = useRef<HTMLDivElement>(null);
  const listRef = useRef<HTMLDivElement>(null);
  const prevHeightRef = useRef<number>(0);
  const scrollPositionRef = useRef<number>(0);

  // Reset when filter key changes (including when cleared)
  useEffect(() => {
    setVisibleCount(chunkSize);
    prevHeightRef.current = 0;
    scrollPositionRef.current = 0;

    // If list ref exists, scroll to top
    if (listRef.current) {
      listRef.current.scrollTop = 0;
    }
  }, [filterKey, chunkSize]);

  // Preserve scroll position when adding new items
  useEffect(() => {
    if (listRef.current && prevHeightRef.current > 0) {
      const newHeight = listRef.current.scrollHeight;
      const heightDifference = newHeight - prevHeightRef.current;

      if (heightDifference > 0 && scrollPositionRef.current > 0) {
        listRef.current.scrollTop = scrollPositionRef.current;
      }

      prevHeightRef.current = newHeight;
    }
  }, [visibleCount]);

  // Save current scroll position and height before loading more
  const handleScroll = useCallback(() => {
    if (listRef.current) {
      scrollPositionRef.current = listRef.current.scrollTop;
      prevHeightRef.current = listRef.current.scrollHeight;
    }
  }, []);

  // Improved observer with better margins
  const handleObserver = useCallback((entries: IntersectionObserverEntry[]) => {
    const [entry] = entries;
    if (entry.isIntersecting && visibleCount < items.length) {
      // Load more items progressively with a smaller batch size for smoother scrolling
      const increment = Math.min(25, items.length - visibleCount);
      setVisibleCount(prev => prev + increment);
    }
  }, [items.length, visibleCount]);

  useEffect(() => {
    const observer = new IntersectionObserver(handleObserver, {
      root: null,
      rootMargin: '200px', // Load earlier before reaching the end
      threshold: 0.1
    });

    if (loaderRef.current) {
      observer.observe(loaderRef.current);
    }

    // Add scroll event listener
    const currentListRef = listRef.current;
    if (currentListRef) {
      currentListRef.addEventListener('scroll', handleScroll, { passive: true });
    }

    return () => {
      observer.disconnect();
      if (currentListRef) {
        currentListRef.removeEventListener('scroll', handleScroll);
      }
    };
  }, [handleObserver, handleScroll]);

  // Reset visible count when items change
  useEffect(() => {
    setVisibleCount(chunkSize);
    prevHeightRef.current = 0;
    scrollPositionRef.current = 0;
  }, [items, chunkSize]);

  return (
    <div ref={listRef} style={{ maxHeight: '400px', overflow: 'auto' }}>
      {items.slice(0, visibleCount).map(renderItem)}
      {visibleCount < items.length && (
        <div
          ref={loaderRef}
          style={{ height: '20px', width: '100%' }}
        />
      )}
    </div>
  );
};

export default VirtualizedList